// Document navigation owns a short-lived scroll transaction. Do not patch native
// scroll APIs: nested chat/modal scrolling must remain independent.
let activeScroll = null;
export const isPageScrollActive = () => activeScroll !== null;

const notify = active => window.dispatchEvent(new CustomEvent('regimen:page-scroll', { detail: { active } }));
const scrollKeys = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

export function runPageScroll(scroll) {
  activeScroll?.finish(false);
  let frame;
  let stableFrames = 0;
  let x = window.scrollX;
  let y = window.scrollY;
  const transaction = { finish };

  function finish(announce = true) {
    if (activeScroll !== transaction) return;
    cancelAnimationFrame(frame);
    window.removeEventListener('wheel', interrupt, true);
    window.removeEventListener('touchmove', interrupt, true);
    window.removeEventListener('pointerdown', interrupt, true);
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('pagehide', onPageHide);
    activeScroll = null;
    if (announce) notify(false);
  }
  function interrupt() {
    // Stop the browser's smooth animation, so its remaining frames cannot be
    // mistaken for manual direction after the user takes over. Do not prevent
    // the input's default action: wheel/touch/keyboard navigation still works.
    window.scrollTo({ top: window.scrollY, left: window.scrollX, behavior: 'instant' });
    finish();
  }
  function onKeyDown(event) {
    if (!scrollKeys.has(event.key) || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target;
    if (target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
    if (event.key === ' ' && target?.closest?.('button, a, [role="button"], [role="link"]')) return;
    interrupt();
  }
  function onPageHide() { finish(); }
  function checkSettled() {
    if (activeScroll !== transaction) return;
    if (window.scrollX === x && window.scrollY === y) stableFrames += 1;
    else stableFrames = 0;
    x = window.scrollX;
    y = window.scrollY;
    // Consecutive quiet animation frames handle smooth, instant, reduced-motion,
    // clamped and no-op scrolls, even in browsers without scrollend. There is no
    // fixed suppression duration and no RAF/listeners left running while idle.
    if (stableFrames >= 3) finish();
    else frame = requestAnimationFrame(checkSettled);
  }

  activeScroll = transaction;
  notify(true);
  window.addEventListener('wheel', interrupt, { capture: true, passive: true });
  window.addEventListener('touchmove', interrupt, { capture: true, passive: true });
  window.addEventListener('pointerdown', interrupt, { capture: true, passive: true });
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('pagehide', onPageHide);
  try {
    scroll();
    frame = requestAnimationFrame(checkSettled);
  } catch (error) {
    finish();
    throw error;
  }
}

export const scrollPageTo = (...args) => runPageScroll(() => window.scrollTo(...args));
export const scrollPageBy = (...args) => runPageScroll(() => window.scrollBy(...args));
export const scrollPageIntoView = (element, options) => runPageScroll(() => element.scrollIntoView(options));
